/**
 * Unified Playback Session (Sprint 6B — UMXP).
 *
 * Single source of truth for the viewer experience orchestration layer.
 * Subsystems (analytics, resume, CW, Experience, plugins) read/patch this
 * session rather than maintaining independent playback identity.
 *
 * Portable: no Pentecost+ API imports. Host adapters hydrate from DTOs.
 */
import { createEntitlementSnapshot } from "./EntitlementModel";
import { PlaybackPlatformEvent } from "./PlaybackPlatformEvents";

var _sessionSeq = 0;

function nextSessionId() {
  _sessionSeq += 1;
  return (
    "ups_" +
    Date.now().toString(36) +
    "_" +
    _sessionSeq.toString(36) +
    "_" +
    Math.floor(Math.random() * 1e6).toString(36)
  );
}

/**
 * @param {object} [seed]
 * @returns {object} session API
 */
export function createPlaybackSession(seed) {
  seed = seed || {};
  var bus = seed.bus || null;

  var data = {
    sessionId: seed.sessionId || nextSessionId(),
    createdAt: seed.createdAt || Date.now(),
    updatedAt: Date.now(),
    pipelinePhase: seed.pipelinePhase || "idle",
    viewer: Object.assign(
      {
        userId: null,
        profileId: null,
        accessTokenPresent: false,
      },
      seed.viewer || {}
    ),
    organization: Object.assign(
      {
        orgId: null,
        brandId: null,
        appId: null,
        whiteLabelKey: null,
      },
      seed.organization || {}
    ),
    content: Object.assign(
      {
        contentId: null,
        title: null,
        mediaKind: "vod", // vod | live | simulive | trailer
        sourceUrl: null,
        poster: null,
        durationHint: null,
      },
      seed.content || {}
    ),
    provider: Object.assign(
      {
        name: null,
        surfaceKind: null,
        capabilities: null,
      },
      seed.provider || {}
    ),
    playback: Object.assign(
      {
        state: "idle",
        currentTime: 0,
        duration: 0,
        muted: false,
        volume: 1,
      },
      seed.playback || {}
    ),
    entitlements: seed.entitlements || createEntitlementSnapshot(),
    intro: Object.assign(
      {
        active: false,
        layerId: null,
        url: null,
        skipVisible: false,
        completed: false,
      },
      seed.intro || {}
    ),
    resume: Object.assign(
      {
        position: 0,
        source: null, // server | local | none
      },
      seed.resume || {}
    ),
    analytics: Object.assign(
      {
        correlationId: null,
        sessionStartedAt: null,
      },
      seed.analytics || {}
    ),
    watchHistory: Object.assign(
      {
        recorded: false,
      },
      seed.watchHistory || {}
    ),
    plugins: Object.assign(
      {
        registered: [],
      },
      seed.plugins || {}
    ),
    experience: Object.assign(
      {
        policyId: null,
        layers: [],
      },
      seed.experience || {}
    ),
    live: Object.assign(
      {
        isLive: false,
        dvrWindowSeconds: null,
        eventStartAt: null,
        countdownActive: false,
        failoverSource: null,
        lowLatency: false,
      },
      seed.live || {}
    ),
  };

  if (!data.analytics.correlationId) {
    data.analytics.correlationId = data.sessionId;
  }

  function snapshot() {
    return JSON.parse(JSON.stringify(data));
  }

  function publish(eventName, payload) {
    if (bus && typeof bus.publish === "function") {
      bus.publish(eventName, payload || snapshot());
    } else if (bus && typeof bus.emit === "function") {
      bus.emit(eventName, payload || snapshot());
    }
  }

  function touch() {
    data.updatedAt = Date.now();
  }

  /**
   * Shallow-merge a section (or top-level keys).
   * @param {string|object} section
   * @param {object} [patch]
   * @param {object} [opts] { silent: boolean }
   */
  function update(section, patch, opts) {
    opts = opts || {};
    if (typeof section === "object" && patch == null) {
      Object.keys(section).forEach(function (k) {
        if (data[k] && typeof data[k] === "object" && !Array.isArray(data[k])) {
          Object.assign(data[k], section[k] || {});
        } else if (section[k] !== undefined) {
          data[k] = section[k];
        }
      });
    } else if (typeof section === "string" && data[section] && typeof data[section] === "object") {
      Object.assign(data[section], patch || {});
    } else if (typeof section === "string") {
      data[section] = patch;
    }
    touch();
    if (!opts.silent) {
      publish(PlaybackPlatformEvent.SESSION_UPDATED, snapshot());
    }
    return snapshot();
  }

  function setPipelinePhase(phase) {
    data.pipelinePhase = phase;
    touch();
    publish(PlaybackPlatformEvent.PIPELINE_PHASE, {
      phase: phase,
      session: snapshot(),
    });
    return phase;
  }

  function setIntroActive(active, meta) {
    meta = meta || {};
    data.intro.active = !!active;
    if (meta.url != null) data.intro.url = meta.url;
    if (meta.layerId != null) data.intro.layerId = meta.layerId;
    if (meta.skipVisible != null) data.intro.skipVisible = !!meta.skipVisible;
    if (!active) data.intro.completed = true;
    touch();
    publish(
      active
        ? PlaybackPlatformEvent.INTRO_STARTED
        : PlaybackPlatformEvent.INTRO_ENDED,
      snapshot()
    );
    return snapshot();
  }

  function setEntitlements(ent) {
    data.entitlements = ent || createEntitlementSnapshot();
    touch();
    publish(PlaybackPlatformEvent.ENTITLEMENT_CHANGED, snapshot());
    return snapshot();
  }

  function setProvider(info) {
    Object.assign(data.provider, info || {});
    touch();
    return snapshot();
  }

  function bindBus(nextBus) {
    bus = nextBus;
  }

  function getId() {
    return data.sessionId;
  }

  function get(section) {
    if (!section) return snapshot();
    return data[section] != null
      ? JSON.parse(JSON.stringify(data[section]))
      : null;
  }

  return {
    getId: getId,
    get: get,
    snapshot: snapshot,
    update: update,
    setPipelinePhase: setPipelinePhase,
    setIntroActive: setIntroActive,
    setEntitlements: setEntitlements,
    setProvider: setProvider,
    bindBus: bindBus,
  };
}

export default createPlaybackSession;
